// 프로필 메뉴 > 설정 — 도메인 알림 설정 (13-calendar-design.md §6).

import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';

import { useAuth } from 'contexts/AuthContext';
import {
  DEFAULT_NOTIFICATION_PREFS,
  saveNotificationPrefs,
  subscribeToCurrentUser,
  type DeadlineLead,
  type NotificationPrefs
} from 'firebase/users-service';

const LEAD_OPTIONS: { value: DeadlineLead; label: string }[] = [
  { value: 'D-7', label: '7일 전부터 매일' },
  { value: 'D-3', label: '3일 전부터 매일' },
  { value: 'D-1', label: '1일 전부터 매일' },
  { value: 'D-0', label: '당일만' },
  { value: 'OFF', label: '끄기' }
];

export default function SettingTab() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    return subscribeToCurrentUser(user.uid, (u) => {
      // 옛 prefs (예: deadlineWarnings) 가 같이 따라오면 다음 저장 때 다시 박혀버림.
      // → 새 필드만 명시적으로 pick. 다음 저장 시 옛 필드는 자연스럽게 사라짐.
      const fromDb = u?.notificationPrefs;
      setPrefs({
        deadlineLead: fromDb?.deadlineLead ?? DEFAULT_NOTIFICATION_PREFS.deadlineLead,
        stagnantEnabled: fromDb?.stagnantEnabled ?? DEFAULT_NOTIFICATION_PREFS.stagnantEnabled
      });
    });
  }, [user]);

  const save = async (next: NotificationPrefs) => {
    if (!user) return;
    setPrefs(next);
    setError(null);
    try {
      await saveNotificationPrefs(user.uid, next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        알림 설정
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          저장 실패: {error}
        </Alert>
      )}

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          사건 진행단계 마감 알림 (카운트다운, 매일)
        </Typography>
        <RadioGroup
          value={prefs.deadlineLead}
          onChange={(e) => save({ ...prefs, deadlineLead: e.target.value as DeadlineLead })}
        >
          <Stack>
            {LEAD_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{opt.label}</Typography>}
                sx={{ mr: 0 }}
              />
            ))}
          </Stack>
        </RadioGroup>
      </Box>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={prefs.stagnantEnabled}
            onChange={(e) => save({ ...prefs, stagnantEnabled: e.target.checked })}
          />
        }
        label={<Typography variant="body2">정체 사건 알림</Typography>}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        90일 이상 변경 없는 진행 중 사건 알림. 마이그레이션(옛 사건 "완료" 처리) 후 켜는 것을 권장합니다.
      </Typography>

      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          💡 메모 알림은 메모 다이얼로그에서 개별 설정합니다.
        </Typography>
      </Box>
    </Box>
  );
}
